// src/cockpit4/components/ProjectDocs.tsx
// ============================================
// P1-功能7：项目文档与知识库（项目中心）
//
// 功能：
//   1. 项目文档管理（按项目分类：需求/设计/测试/验收 四类）
//   2. 文档模板库（内置模板：需求文档/设计文档/测试计划/验收报告 模板）
//   3. 自动生成项目文档（选择项目+模板，生成文档，写入 Vault）
//   4. 文档版本管理（记录编辑历史，localStorage）
//   5. 项目知识自动沉淀到企业知识库（一键沉淀到知识中心）
//
// 数据来源：
//   - localStorage 持久化文档元数据（xeaos_project_docs）
//   - localStorage 读取关联项目（xeaos_projects_custom）
//   - Vault 写入文档内容（XEAOS/Projects/{项目名}/{文档类型}-{date}.md）
//   - Vault 写入知识沉淀（09_Knowledge（知识中心）/Projects/{项目名}.md）
// ============================================

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Plus,
  X,
  RefreshCw,
  Loader2,
  Trash2,
  Pencil,
  BookOpen,
  Sparkles,
  Download,
  History,
  FileCheck,
  FileCode,
  ClipboardCheck,
  FlaskConical,
  ExternalLink,
  Layers,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

/** 文档类型 */
export type DocType = "requirement" | "design" | "test" | "acceptance";

/** 文档版本记录 */
export interface DocVersion {
  version: number;
  /** 该版本摘要或全文 */
  summary: string;
  author: string;
  createdAt: string;
}

/** 文档元数据 */
export interface ProjectDoc {
  id: string;
  projectId: string;
  projectName: string;
  type: DocType;
  title: string;
  /** Vault 存储路径 */
  vaultPath: string;
  /** 版本历史 */
  versions: DocVersion[];
  createdAt: string;
  updatedAt: string;
}

/** 项目（简化结构，从 localStorage 读取） */
interface ProjectLite {
  id: string;
  name: string;
  description?: string;
  owner?: string;
  startDate?: string;
  dueDate?: string;
  progress?: number;
}

// ============================================
// 常量配置
// ============================================

const DOCS_KEY = "xeaos_project_docs";
const PROJECTS_KEY = "xeaos_projects_custom";

const DOC_TYPE_META: Record<
  DocType,
  { label: string; color: string; icon: typeof FileText; dir: string }
> = {
  requirement: {
    label: "需求文档",
    color: "#3B82F6",
    icon: FileCheck,
    dir: "需求文档",
  },
  design: {
    label: "设计文档",
    color: "#A78BFA",
    icon: FileCode,
    dir: "设计文档",
  },
  test: {
    label: "测试计划",
    color: "#F59E0B",
    icon: FlaskConical,
    dir: "测试计划",
  },
  acceptance: {
    label: "验收报告",
    color: "#10B981",
    icon: ClipboardCheck,
    dir: "验收报告",
  },
};

/** 内置文档模板：返回 Markdown 内容 */
const DOC_TEMPLATES: Record<
  DocType,
  (project: ProjectLite, date: string) => string
> = {
  requirement: (p, date) => `# ${p.name} · 需求文档

> 生成日期：${date} · 负责人：${p.owner || "—"}

## 1. 项目概述

${p.description || "请补充项目背景与目标。"}

## 2. 项目目标

- 目标 1：（请补充）
- 目标 2：（请补充）

## 3. 干系人

| 角色 | 姓名 | 职责 |
| --- | --- | --- |
| 项目负责人 | ${p.owner || "—"} | 整体协调 |
| 产品 | — | 需求澄清 |
| 开发 | — | 方案实现 |
| 测试 | — | 质量保障 |

## 4. 功能需求

### 4.1 功能模块一

- **需求编号**：REQ-001
- **优先级**：高
- **描述**：（请补充）
- **验收标准**：（请补充）

## 5. 非功能需求

- 性能：（请补充）
- 安全：（请补充）
- 兼容性：（请补充）

## 6. 时间计划

- 开始日期：${p.startDate || "—"}
- 截止日期：${p.dueDate || "—"}
- 当前进度：${p.progress ?? 0}%

## 7. 风险与依赖

- （请补充）

---

*本文档由 XEAOS 项目中心 · 文档模板库自动生成*`,
  design: (p, date) => `# ${p.name} · 设计文档

> 生成日期：${date} · 负责人：${p.owner || "—"}

## 1. 设计概述

${p.description || "请补充设计目标与范围。"}

## 2. 系统架构

### 2.1 架构图

（请补充架构图）

### 2.2 技术选型

| 层级 | 技术 | 说明 |
| --- | --- | --- |
| 前端 | — | — |
| 后端 | — | — |
| 数据库 | — | — |
| 部署 | — | — |

## 3. 模块设计

### 3.1 模块一

- 职责：（请补充）
- 接口：（请补充）
- 数据模型：（请补充）

## 4. 数据模型

### 4.1 实体一

| 字段 | 类型 | 必填 | 说明 |
| --- | --- | --- | --- |
| id | string | 是 | 主键 |

## 5. 接口设计

### 5.1 接口一

- **路径**：/api/xxx
- **方法**：GET
- **请求参数**：（请补充）
- **响应**：（请补充）

## 6. 部署方案

- （请补充）

---

*本文档由 XEAOS 项目中心 · 文档模板库自动生成*`,
  test: (p, date) => `# ${p.name} · 测试计划

> 生成日期：${date} · 负责人：${p.owner || "—"}

## 1. 测试目标

${p.description || "请补充测试目标。"}

## 2. 测试范围

### 2.1 功能测试

- 测试模块一：（请补充用例数）

### 2.2 性能测试

- 并发量：（请补充）
- 响应时间要求：（请补充）

### 2.3 兼容性测试

- 浏览器：（请补充）
- 设备：（请补充）

## 3. 测试策略

| 阶段 | 类型 | 工具 | 负责人 |
| --- | --- | --- | --- |
| 单元测试 | 单元 | Jest | — |
| 集成测试 | 集成 | — | — |
| 系统测试 | 系统 | — | — |
| 验收测试 | UAT | — | — |

## 4. 测试用例

### 4.1 用例 TC-001

- **模块**：（请补充）
- **前置条件**：（请补充）
- **步骤**：（请补充）
- **预期结果**：（请补充）

## 5. 缺陷管理

- 缺陷分级：致命 / 严重 / 轻微
- 缺陷工具：（请补充）

## 6. 时间安排

- 开始日期：${p.startDate || "—"}
- 截止日期：${p.dueDate || "—"}

---

*本文档由 XEAOS 项目中心 · 文档模板库自动生成*`,
  acceptance: (p, date) => `# ${p.name} · 验收报告

> 生成日期：${date} · 负责人：${p.owner || "—"}

## 1. 验收概述

${p.description || "请补充验收背景。"}

## 2. 验收标准

| 序号 | 验收项 | 标准 | 结果 |
| --- | --- | --- | --- |
| 1 | 功能完整性 | 全部需求实现 | ☐ 通过 ☐ 不通过 |
| 2 | 性能指标 | 满足性能要求 | ☐ 通过 ☐ 不通过 |
| 3 | 缺陷修复 | 致命/严重缺陷为 0 | ☐ 通过 ☐ 不通过 |
| 4 | 文档完整性 | 文档齐备 | ☐ 通过 ☐ 不通过 |

## 3. 验收结果

### 3.1 功能验收

- 验收日期：${date}
- 当前进度：${p.progress ?? 0}%
- 验收结论：☐ 通过 ☐ 不通过

### 3.2 缺陷统计

- 致命缺陷：（请补充）
- 严重缺陷：（请补充）
- 轻微缺陷：（请补充）

## 4. 遗留问题

| 序号 | 问题描述 | 严重程度 | 处理计划 |
| --- | --- | --- | --- |
| 1 | （请补充） | — | — |

## 5. 验收结论

- 综合结论：☐ 通过验收 ☐ 附条件通过 ☐ 不通过
- 验收人：（请补充）
- 日期：${date}

## 6. 附件

- 需求文档：（链接）
- 设计文档：（链接）
- 测试报告：（链接）

---

*本文档由 XEAOS 项目中心 · 文档模板库自动生成*`,
};

const VAULT_DOCS_ROOT = "XEAOS/Projects";
const VAULT_KNOWLEDGE_ROOT = "09_Knowledge（知识中心）/Projects";

// ============================================
// localStorage 持久化
// ============================================

function loadDocs(): ProjectDoc[] {
  try {
    const raw = localStorage.getItem(DOCS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as ProjectDoc[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

function saveDocs(docs: ProjectDoc[]): void {
  try {
    localStorage.setItem(DOCS_KEY, JSON.stringify(docs));
  } catch {
    // 静默降级
  }
}

function loadProjects(): ProjectLite[] {
  try {
    const raw = localStorage.getItem(PROJECTS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as ProjectLite[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

// ============================================
// 工具函数
// ============================================

function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

/** 写入 Vault 文档 */
async function writeVaultDoc(
  path: string,
  content: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const resp = await fetch("/vault-api/write", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path, content }),
    });
    if (resp.ok) {
      const json = await resp.json();
      if (json?.success !== false) {
        return { success: true };
      }
      return { success: false, error: "Vault 写入返回非成功" };
    }
    return { success: false, error: `HTTP ${resp.status}` };
  } catch (err: any) {
    return { success: false, error: err?.message || String(err) };
  }
}

// ============================================
// 主组件
// ============================================

export default function ProjectDocs() {
  const [docs, setDocs] = useState<ProjectDoc[]>([]);
  const [projects, setProjects] = useState<ProjectLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState<ProjectDoc | null>(
    null
  );
  const [status, setStatus] = useState<{
    loading: boolean;
    message: string | null;
    kind: "info" | "error" | "success";
  }>({ loading: false, message: null, kind: "info" });
  const [typeFilter, setTypeFilter] = useState<DocType | "all">("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");

  // ============================================
  // 加载数据
  // ============================================
  const loadData = useCallback(() => {
    setLoading(true);
    setDocs(loadDocs());
    setProjects(loadProjects());
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============================================
  // 生成文档（选择项目 + 模板 → 写入 Vault）
  // ============================================
  const handleGenerateDoc = useCallback(
    async (data: {
      projectId: string;
      type: DocType;
      title: string;
      author: string;
      summary: string;
    }) => {
      const project = projects.find((p) => p.id === data.projectId);
      if (!project) {
        setStatus({
          loading: false,
          message: "未找到所选项目",
          kind: "error",
        });
        return;
      }
      setStatus({ loading: true, message: null, kind: "info" });
      const today = formatDate(new Date().toISOString());
      const typeMeta = DOC_TYPE_META[data.type];
      const content = DOC_TEMPLATES[data.type](project, today);
      // 在模板顶部追加版本与作者信息
      const finalContent = `<!-- 版本：v1 · 作者：${data.author || "—"} · 日期：${today} -->\n${content}`;
      const vaultPath = `${VAULT_DOCS_ROOT}/${project.name}/${typeMeta.dir}-${today}.md`;

      const result = await writeVaultDoc(vaultPath, finalContent);
      if (!result.success) {
        setStatus({
          loading: false,
          message: `✕ Vault 写入失败：${result.error}`,
          kind: "error",
        });
        return;
      }

      // 保存文档元数据
      const now = new Date().toISOString();
      const newDoc: ProjectDoc = {
        id: `doc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        projectId: project.id,
        projectName: project.name,
        type: data.type,
        title: data.title,
        vaultPath,
        versions: [
          {
            version: 1,
            summary: data.summary || `初始版本，基于${typeMeta.label}模板生成`,
            author: data.author || "—",
            createdAt: now,
          },
        ],
        createdAt: now,
        updatedAt: now,
      };
      const updated = [newDoc, ...docs];
      setDocs(updated);
      saveDocs(updated);
      setStatus({
        loading: false,
        message: `✓ 文档已生成并写入 Vault：${vaultPath}`,
        kind: "success",
      });
      setShowGenerateModal(false);
    },
    [projects, docs]
  );

  // ============================================
  // 删除文档
  // ============================================
  const handleDeleteDoc = useCallback(
    (docId: string) => {
      const updated = docs.filter((d) => d.id !== docId);
      setDocs(updated);
      saveDocs(updated);
    },
    [docs]
  );

  // ============================================
  // 追加版本（重新生成 / 更新文档内容）
  // ============================================
  const handleAddVersion = useCallback(
    async (doc: ProjectDoc, summary: string, author: string) => {
      setStatus({ loading: true, message: null, kind: "info" });
      const today = formatDate(new Date().toISOString());
      const project = projects.find((p) => p.id === doc.projectId);
      if (!project) {
        setStatus({
          loading: false,
          message: "未找到关联项目",
          kind: "error",
        });
        return;
      }
      const typeMeta = DOC_TYPE_META[doc.type];
      const content = DOC_TEMPLATES[doc.type](project, today);
      const newVersion = doc.versions.length + 1;
      const finalContent = `<!-- 版本：v${newVersion} · 作者：${author || "—"} · 日期：${today} · 摘要：${summary} -->\n${content}`;
      // 覆盖写入同一路径（同一天）或带版本号
      const vaultPath = `${VAULT_DOCS_ROOT}/${project.name}/${typeMeta.dir}-${today}-v${newVersion}.md`;
      const result = await writeVaultDoc(vaultPath, finalContent);
      if (!result.success) {
        setStatus({
          loading: false,
          message: `✕ 版本更新失败：${result.error}`,
          kind: "error",
        });
        return;
      }
      const now = new Date().toISOString();
      const updatedDoc: ProjectDoc = {
        ...doc,
        vaultPath,
        versions: [
          ...doc.versions,
          { version: newVersion, summary, author: author || "—", createdAt: now },
        ],
        updatedAt: now,
      };
      const updated = docs.map((d) => (d.id === doc.id ? updatedDoc : d));
      setDocs(updated);
      saveDocs(updated);
      setStatus({
        loading: false,
        message: `✓ 新版本 v${newVersion} 已写入：${vaultPath}`,
        kind: "success",
      });
      setShowVersionModal(null);
    },
    [docs, projects]
  );

  // ============================================
  // 知识沉淀（一键写入知识中心）
  // ============================================
  const handleDistillKnowledge = useCallback(
    async (project: ProjectLite) => {
      setStatus({ loading: true, message: null, kind: "info" });
      const today = formatDate(new Date().toISOString());
      const projectDocs = docs.filter((d) => d.projectId === project.id);
      const md = buildKnowledgeMarkdown(project, projectDocs, today);
      const vaultPath = `${VAULT_KNOWLEDGE_ROOT}/${project.name}.md`;
      const result = await writeVaultDoc(vaultPath, md);
      if (!result.success) {
        setStatus({
          loading: false,
          message: `✕ 知识沉淀失败：${result.error}`,
          kind: "error",
        });
        return;
      }
      setStatus({
        loading: false,
        message: `✓ 项目知识已沉淀到知识中心：${vaultPath}`,
        kind: "success",
      });
    },
    [docs]
  );

  // ============================================
  // 筛选与统计
  // ============================================
  const filteredDocs = useMemo(() => {
    return docs.filter((d) => {
      if (typeFilter !== "all" && d.type !== typeFilter) return false;
      if (projectFilter !== "all" && d.projectId !== projectFilter) return false;
      return true;
    });
  }, [docs, typeFilter, projectFilter]);

  const stats = useMemo(() => {
    const byType: Record<DocType, number> = {
      requirement: 0,
      design: 0,
      test: 0,
      acceptance: 0,
    };
    docs.forEach((d) => byType[d.type]++);
    return {
      total: docs.length,
      byType,
      projects: projects.length,
    };
  }, [docs, projects]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-indigo-500/15 border border-indigo-500/30">
            <BookOpen size={13} className="text-indigo-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              项目文档与知识库
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Project Docs · {stats.total} 文档 · {stats.projects} 项目 ·
              需求 {stats.byType.requirement} · 设计 {stats.byType.design} ·
              测试 {stats.byType.test} · 验收 {stats.byType.acceptance}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 生成文档 */}
          <button
            onClick={() => setShowGenerateModal(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-500/15 border border-indigo-500/30 hover:bg-indigo-500/25 transition-colors"
          >
            <Sparkles size={11} className="text-indigo-400" />
            <span className="text-[9px] font-mono text-indigo-300 uppercase tracking-wider">
              生成文档
            </span>
          </button>

          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={11} className="text-gray-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* 状态消息 */}
      {status.message && (
        <div
          className={`px-4 py-1.5 border-b border-white/[0.04] ${
            status.kind === "error"
              ? "bg-red-500/[0.04]"
              : status.kind === "success"
              ? "bg-emerald-500/[0.04]"
              : "bg-indigo-500/[0.04]"
          }`}
        >
          <span
            className={`text-[9px] font-mono ${
              status.kind === "error"
                ? "text-red-300"
                : status.kind === "success"
                ? "text-emerald-300"
                : "text-indigo-300"
            }`}
          >
            {status.message}
          </span>
        </div>
      )}

      {/* 筛选栏 */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-white/[0.04]">
        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          className="px-2 py-1 rounded-md bg-white/[0.02] border border-white/[0.04] text-[9px] font-mono text-gray-400 outline-none cursor-pointer"
        >
          <option value="all">全部项目</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as DocType | "all")}
          className="px-2 py-1 rounded-md bg-white/[0.02] border border-white/[0.04] text-[9px] font-mono text-gray-400 outline-none cursor-pointer"
        >
          <option value="all">全部类型</option>
          {(Object.keys(DOC_TYPE_META) as DocType[]).map((t) => (
            <option key={t} value={t}>
              {DOC_TYPE_META[t].label}
            </option>
          ))}
        </select>
        <span className="ml-auto text-[9px] font-mono text-gray-600">
          {filteredDocs.length}/{docs.length}
        </span>
      </div>

      {/* 主体内容 */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 size={20} className="animate-spin text-indigo-400" />
            <span className="ml-2 text-[11px] font-mono text-gray-500">
              加载文档数据...
            </span>
          </div>
        ) : docs.length === 0 ? (
          <EmptyState
            onGenerate={() => setShowGenerateModal(true)}
            hint={
              projects.length === 0
                ? "请先在「项目看板」中创建项目，再生成文档"
                : "点击「生成文档」基于模板创建第一份项目文档"
            }
          />
        ) : (
          <div className="space-y-1.5 max-h-[460px] overflow-y-auto">
            {filteredDocs.map((d) => (
              <DocRow
                key={d.id}
                doc={d}
                onShowVersions={() => setShowVersionModal(d)}
                onDelete={() => handleDeleteDoc(d.id)}
              />
            ))}
          </div>
        )}

        {/* 知识沉淀区：项目列表 */}
        {projects.length > 0 && (
          <div className="mt-4 pt-3 border-t border-white/[0.04]">
            <div className="flex items-center gap-1.5 mb-2">
              <Layers size={11} className="text-indigo-400" />
              <span className="text-[10px] font-mono text-indigo-400 uppercase tracking-wider">
                知识沉淀（一键写入企业知识库）
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {projects.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded border border-white/[0.04] bg-white/[0.02]"
                >
                  <FileText size={10} className="text-gray-500 flex-shrink-0" />
                  <span className="text-[10px] text-gray-300 truncate flex-1">
                    {p.name}
                  </span>
                  <button
                    onClick={() => handleDistillKnowledge(p)}
                    disabled={status.loading}
                    className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/25 transition-colors disabled:opacity-50"
                    title="沉淀到 09_Knowledge（知识中心）/Projects/"
                  >
                    <Download size={8} />
                    沉淀
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 生成文档弹窗 */}
      <AnimatePresence>
        {showGenerateModal && (
          <GenerateDocModal
            projects={projects}
            onSubmit={handleGenerateDoc}
            onClose={() => setShowGenerateModal(false)}
          />
        )}
      </AnimatePresence>

      {/* 版本历史弹窗 */}
      <AnimatePresence>
        {showVersionModal && (
          <VersionModal
            doc={showVersionModal}
            onAddVersion={(summary, author) =>
              handleAddVersion(showVersionModal, summary, author)
            }
            onClose={() => setShowVersionModal(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 生成知识沉淀 Markdown
// ============================================
function buildKnowledgeMarkdown(
  project: ProjectLite,
  projectDocs: ProjectDoc[],
  date: string
): string {
  const lines: string[] = [];
  lines.push(`# ${project.name} · 项目知识沉淀`);
  lines.push("");
  lines.push(`> 沉淀日期：${date} · 由 ProjectDocs 一键生成`);
  lines.push("");
  lines.push("## 项目概述");
  lines.push("");
  lines.push(project.description || "（暂无描述）");
  lines.push("");
  lines.push("## 基本信息");
  lines.push("");
  lines.push(`- 负责人：${project.owner || "—"}`);
  lines.push(`- 开始日期：${project.startDate || "—"}`);
  lines.push(`- 截止日期：${project.dueDate || "—"}`);
  lines.push(`- 当前进度：${project.progress ?? 0}%`);
  lines.push("");

  lines.push("## 文档清单");
  lines.push("");
  if (projectDocs.length === 0) {
    lines.push("暂无项目文档。");
  } else {
    lines.push("| 类型 | 标题 | Vault 路径 | 版本数 | 最近更新 |");
    lines.push("| --- | --- | --- | --- | --- |");
    projectDocs.forEach((d) => {
      lines.push(
        `| ${DOC_TYPE_META[d.type].label} | ${d.title} | ${d.vaultPath} | ${d.versions.length} | ${formatDate(d.updatedAt)} |`
      );
    });
  }
  lines.push("");

  lines.push("## 版本历史摘要");
  lines.push("");
  projectDocs.forEach((d) => {
    lines.push(`### ${d.title}`);
    d.versions.forEach((v) => {
      lines.push(
        `- **v${v.version}**（${formatDate(v.createdAt)}）· ${v.author}：${v.summary}`
      );
    });
  });
  lines.push("");

  lines.push("## 经验沉淀");
  lines.push("");
  lines.push("- 项目关键决策：（请补充）");
  lines.push("- 踩坑与教训：（请补充）");
  lines.push("- 可复用资产：（请补充）");
  lines.push("");

  lines.push("---");
  lines.push("");
  lines.push("*本文档由 XEAOS 项目中心 · 知识沉淀功能自动生成*");
  return lines.join("\n");
}

// ============================================
// 空状态
// ============================================
function EmptyState({
  onGenerate,
  hint,
}: {
  onGenerate: () => void;
  hint: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <BookOpen size={32} className="text-gray-700 mb-2" />
      <p className="text-[12px] text-gray-500 mb-1">暂无项目文档</p>
      <p className="text-[10px] font-mono text-gray-600 mb-3">{hint}</p>
      <button
        onClick={onGenerate}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-500/15 border border-indigo-500/30 hover:bg-indigo-500/25 transition-colors"
      >
        <Sparkles size={12} className="text-indigo-400" />
        <span className="text-[10px] font-mono text-indigo-300 uppercase tracking-wider">
          生成文档
        </span>
      </button>
    </div>
  );
}

// ============================================
// 文档行
// ============================================
function DocRow({
  doc,
  onShowVersions,
  onDelete,
}: {
  doc: ProjectDoc;
  onShowVersions: () => void;
  onDelete: () => void;
}) {
  const typeMeta = DOC_TYPE_META[doc.type];
  const TIcon = typeMeta.icon;
  return (
    <div
      className="rounded-md border bg-white/[0.02] p-2.5"
      style={{ borderColor: `${typeMeta.color}30` }}
    >
      <div className="flex items-center gap-2">
        {/* 类型图标 */}
        <div
          className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0"
          style={{
            background: `${typeMeta.color}15`,
            border: `1px solid ${typeMeta.color}30`,
          }}
        >
          <TIcon size={12} style={{ color: typeMeta.color }} />
        </div>

        {/* 标题 + 项目 */}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-gray-200 font-medium truncate">
            {doc.title}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className="px-1 rounded text-[8px] font-mono uppercase"
              style={{
                background: `${typeMeta.color}20`,
                color: typeMeta.color,
              }}
            >
              {typeMeta.label}
            </span>
            <span className="text-[9px] font-mono text-gray-600 truncate">
              {doc.projectName}
            </span>
          </div>
        </div>

        {/* 版本数 */}
        <div className="flex-shrink-0 text-center w-[50px]">
          <div className="text-[8px] font-mono text-gray-600 uppercase">版本</div>
          <div className="text-[12px] font-mono text-gray-300">
            v{doc.versions.length}
          </div>
        </div>

        {/* 更新时间 */}
        <div className="flex-shrink-0 text-center w-[80px]">
          <div className="text-[8px] font-mono text-gray-600 uppercase">更新</div>
          <div className="text-[9px] font-mono text-gray-400">
            {formatDate(doc.updatedAt)}
          </div>
        </div>

        {/* Vault 路径预览 */}
        <div className="flex-shrink-0 max-w-[180px]">
          <div className="text-[8px] font-mono text-gray-600 truncate" title={doc.vaultPath}>
            {doc.vaultPath}
          </div>
        </div>

        {/* 操作 */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={onShowVersions}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-indigo-400 transition-colors"
            title="版本历史 / 追加版本"
          >
            <History size={9} />
          </button>
          <button
            onClick={onDelete}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-red-400 transition-colors"
            title="删除文档元数据"
          >
            <Trash2 size={9} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 生成文档弹窗
// ============================================
function GenerateDocModal({
  projects,
  onSubmit,
  onClose,
}: {
  projects: ProjectLite[];
  onSubmit: (data: {
    projectId: string;
    type: DocType;
    title: string;
    author: string;
    summary: string;
  }) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    projectId: projects[0]?.id || "",
    type: "requirement" as DocType,
    title: "",
    author: "",
    summary: "",
  });
  const [error, setError] = useState<string | null>(null);

  const selectedProject = projects.find((p) => p.id === form.projectId);
  const typeMeta = DOC_TYPE_META[form.type];

  const handleSubmit = () => {
    if (!form.projectId) {
      setError("请选择关联项目");
      return;
    }
    if (!form.title.trim()) {
      setError("请输入文档标题");
      return;
    }
    onSubmit(form);
  };

  // 若标题为空，按类型自动建议
  const suggestedTitle = selectedProject
    ? `${selectedProject.name} · ${typeMeta.label}`
    : typeMeta.label;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md max-h-[85vh] flex flex-col rounded-xl border border-indigo-500/30 bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
              <Sparkles size={13} className="text-indigo-400" />
            </div>
            <h3 className="text-[13px] font-semibold text-white">生成项目文档</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 表单 */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {projects.length === 0 ? (
            <div className="px-3 py-3 rounded border border-amber-500/30 bg-amber-500/10 text-[10px] text-amber-300 font-mono">
              ⚠ 暂无项目，请先在「项目看板」中创建项目
            </div>
          ) : (
            <>
              {error && (
                <div className="px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-[10px] text-red-300 font-mono">
                  {error}
                </div>
              )}

              <FormField label="关联项目">
                <select
                  value={form.projectId}
                  onChange={(e) =>
                    setForm({ ...form, projectId: e.target.value })
                  }
                  className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none cursor-pointer"
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="文档类型（模板）">
                <div className="grid grid-cols-2 gap-1.5">
                  {(Object.keys(DOC_TYPE_META) as DocType[]).map((t) => {
                    const meta = DOC_TYPE_META[t];
                    const Icon = meta.icon;
                    const isActive = form.type === t;
                    return (
                      <button
                        key={t}
                        onClick={() => setForm({ ...form, type: t })}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md border text-[10px] font-mono transition-colors ${
                          isActive
                            ? "bg-indigo-500/15 text-indigo-300"
                            : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
                        }`}
                        style={
                          isActive
                            ? { borderColor: `${meta.color}50` }
                            : { borderColor: "#ffffff10" }
                        }
                      >
                        <Icon size={11} style={{ color: meta.color }} />
                        {meta.label}
                      </button>
                    );
                  })}
                </div>
              </FormField>

              <FormField label="文档标题">
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={suggestedTitle}
                  className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-indigo-500/30"
                />
              </FormField>

              <FormField label="作者">
                <input
                  type="text"
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                  placeholder="文档作者"
                  className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-indigo-500/30"
                />
              </FormField>

              <FormField label="版本摘要（v1）">
                <input
                  type="text"
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  placeholder={`如：基于${typeMeta.label}模板生成初始版本`}
                  className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-indigo-500/30"
                />
              </FormField>

              {/* 路径预览 */}
              <div className="px-2 py-1.5 rounded bg-white/[0.02] border border-white/[0.04]">
                <div className="text-[8px] font-mono text-gray-600 uppercase tracking-wider mb-0.5">
                  Vault 写入路径
                </div>
                <div className="text-[9px] font-mono text-gray-400 break-all">
                  {selectedProject
                    ? `${VAULT_DOCS_ROOT}/${selectedProject.name}/${typeMeta.dir}-${formatDate(new Date().toISOString())}.md`
                    : "请先选择项目"}
                </div>
              </div>
            </>
          )}
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06] bg-white/[0.01]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-white/[0.06] text-[10px] font-mono text-gray-400 hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={projects.length === 0}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-indigo-500/20 border border-indigo-500/30 text-[10px] font-mono text-indigo-300 hover:bg-indigo-500/30 transition-colors disabled:opacity-50"
          >
            <Download size={10} />
            生成并写入 Vault
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 版本历史弹窗
// ============================================
function VersionModal({
  doc,
  onAddVersion,
  onClose,
}: {
  doc: ProjectDoc;
  onAddVersion: (summary: string, author: string) => void;
  onClose: () => void;
}) {
  const [summary, setSummary] = useState("");
  const [author, setAuthor] = useState("");
  const typeMeta = DOC_TYPE_META[doc.type];
  const TIcon = typeMeta.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-xl border border-indigo-500/30 bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
              <History size={13} className="text-indigo-400" />
            </div>
            <div>
              <h3 className="text-[13px] font-semibold text-white">
                版本历史 · 追加版本
              </h3>
              <p className="text-[9px] font-mono text-gray-500 truncate max-w-[280px]">
                {doc.title}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 内容 */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {/* 当前 Vault 路径 */}
          <div className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-white/[0.02] border border-white/[0.04]">
            <TIcon size={10} style={{ color: typeMeta.color }} />
            <span className="text-[9px] font-mono text-gray-400 break-all">
              {doc.vaultPath}
            </span>
          </div>

          {/* 历史版本列表 */}
          <div>
            <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
              版本历史（{doc.versions.length}）
            </div>
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {[...doc.versions].reverse().map((v) => (
                <div
                  key={v.version}
                  className="flex items-start gap-2 px-2 py-1.5 rounded bg-white/[0.02] border border-white/[0.04]"
                >
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-indigo-500/15 text-indigo-300 flex-shrink-0">
                    v{v.version}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] text-gray-300 break-words">
                      {v.summary}
                    </div>
                    <div className="text-[8px] font-mono text-gray-600 mt-0.5">
                      {v.author} · {formatDate(v.createdAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 追加新版本 */}
          <div className="pt-2 border-t border-white/[0.04]">
            <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
              追加新版本（v{doc.versions.length + 1}，将重新生成模板并写入新 Vault 文件）
            </div>
            <FormField label="作者">
              <input
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="更新作者"
                className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-indigo-500/30"
              />
            </FormField>
            <div className="mt-2">
              <FormField label="版本摘要">
                <input
                  type="text"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder="本次更新内容摘要"
                  className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-indigo-500/30"
                />
              </FormField>
            </div>
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06] bg-white/[0.01]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-white/[0.06] text-[10px] font-mono text-gray-400 hover:bg-white/[0.04] transition-colors"
          >
            关闭
          </button>
          <button
            onClick={() => {
              if (!summary.trim()) return;
              onAddVersion(summary, author);
            }}
            disabled={!summary.trim()}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-indigo-500/20 border border-indigo-500/30 text-[10px] font-mono text-indigo-300 hover:bg-indigo-500/30 transition-colors disabled:opacity-50"
          >
            <Plus size={10} />
            追加版本
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 表单字段包装
// ============================================
function FormField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
